import React from "react";
import { Box, Row, Col } from "jsxstyle";
import Button from "components/Button";
import Separator from "components/Separator";
import FormGrid from "components/FormGrid";
import FormGroup from "components/FormGroup";
import SelectEntityMaster from "components/SelectEntityMaster";
import CurrencyInputGroup from "components/CurrencyInputGroup";
import Form from "components/Form";
import useToast from "hooks/useToast";
import useForm from "hooks/useForm";
import isRequired from "validators/isRequired";
import useAsync from "hooks/useAsync";
import saveCLOUnderwriterService from "services/clo/saveCLOUnderwriterService";
import SelectUnderwriterRoleCode from "components/SelectUnderwriterRoleCode";
import CurrencyFormat from "components/CurrencyFormat";
import { get } from "lodash";

function CMBSUnderwritersForm({
  onData,
  initialValues,
  onCancel,
  dealSize,
  bookRunnerCredit,
}) {
  const { showSuccess, showError } = useToast();

  const {
    getValue,
    setValue,
    setValues,
    getValues,
    getError,
    submit,
    reset,
  } = useForm({
    onSubmit: (values) =>
      fetch({
        values,
        dealCode: values.dealCode,
        underwriterId: values.id,
      }),
    initialValues: {
      ...(initialValues || {}),
      amount_percentage: Number(
        (((initialValues.amount || 0) / dealSize) * 100).toFixed(4),
      ),
    },
    validations: {
      entity_id: [isRequired],
      entity_type: [isRequired],
      // amount: [isRequired]
    },
  });

  const [fetch, isSubmitting] = useAsync({
    promiseFn: saveCLOUnderwriterService,
    onData: () => {
      showSuccess("Underwriters saved.");
      reset();
      onData();
    },
    onError: (error) => {
      const type = get(error, "data.error");
      showError(
        {
          duplicated_underwriter_entity_and_type:
            "Duplicated entity found for underwriter role",
        }[type] || "An error happened.",
      );
    },
  });

  const handleCancel = React.useCallback(() => {
    onCancel();
    reset(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isEditing = Boolean(getValue("id"));

  return (
    <Form onSubmit={() => submit()}>
      <FormGrid>
        <Box className="col-3">
          <FormGroup
            label="Underwriter"
            disabled={isSubmitting}
            error={getError("entity_id")}
          >
            <SelectEntityMaster
              onSelect={(value) => setValue("entity_id", value)}
              value={getValue("entity_id")}
              error={getError("entity_id")}
              disabled={isSubmitting}
            />
          </FormGroup>
        </Box>
        <Box className="col-3">
          <FormGroup
            label="Role"
            disabled={isSubmitting}
            error={getError("entity_type")}
          >
            <SelectUnderwriterRoleCode
              onSelect={(value) => setValue("entity_type", value)}
              value={getValue("entity_type")}
              error={getError("entity_type")}
              disabled={isSubmitting}
            />
          </FormGroup>
        </Box>
        <Box className="col-3">
          <FormGroup
            key={getValue("amountKey")}
            label="Amount"
            disabled={isSubmitting}
            error={getError("amount")}
          >
            <CurrencyInputGroup
              leftIcon="dollar"
              intent={getError("amount") ? "danger" : null}
              value={getValue("amount")}
              onChange={(value) =>
                setValues({
                  ...getValues(),
                  amount: value,
                  amount_percentage: Number(
                    ((value / dealSize) * 100).toFixed(4),
                  ),
                  percentageKey: getValue("percentageKey", 0) + 1,
                })
              }
              disabled={isSubmitting}
            />
          </FormGroup>
        </Box>
        <Box className="col-3">
          <FormGroup
            key={getValue("percentageKey")}
            label="Amount(%)"
            disabled={isSubmitting}
            error={getError("amount_percentage")}
          >
            <CurrencyInputGroup
              leftIcon="percentage"
              value={getValue("amount_percentage")}
              onChange={(value) =>
                setValues({
                  ...getValues(),
                  amount: Number(((value * dealSize) / 100).toFixed(4)),
                  amount_percentage: value,
                  amountKey: getValue("amountKey", 0) + 1,
                })
              }
              disabled={isSubmitting}
            />
          </FormGroup>
        </Box>
        <Row className="col-12">
          <Row flex={1} alignItems="flex-start">
            {isEditing && (
              <React.Fragment>
                <Button
                  text="Cancel"
                  onClick={handleCancel}
                  disabled={isSubmitting}
                />
                <Separator />
              </React.Fragment>
            )}
            <Button
              type="submit"
              text={isEditing ? "Save" : "Add"}
              intent="success"
              disabled={isSubmitting}
              loading={isSubmitting}
            />
          </Row>
          <Col alignItems="flex-end">
            <Box component="p">
              Deal size:&nbsp;
              <Box component="strong">
                <CurrencyFormat amount={dealSize} />
              </Box>
            </Box>
            <Box component="p">
              Remaining bookrunner credit:&nbsp;
              <Box component="strong">
                <CurrencyFormat amount={dealSize - bookRunnerCredit} />
              </Box>
            </Box>
          </Col>
        </Row>
      </FormGrid>
    </Form>
  );
}

CMBSUnderwritersForm.propTypes = {};

CMBSUnderwritersForm.defaultProps = {};

export default CMBSUnderwritersForm;
